import { NavLink, Outlet, useNavigate, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "../contexts/AuthContext";
import { LayoutDashboard, FilePlus, BookOpen, LogOut, Shield, Zap, Home, Users, Settings as SettingsIcon } from "lucide-react";
import LanguageSwitcher from "./LanguageSwitcher";
import NotificationsBell from "./NotificationsBell";

export default function MerchantLayout() {
  const { t } = useTranslation();
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const NAV = [
    { to: "/merchant", label: t("merchant_nav.my_cover"), icon: LayoutDashboard, end: true, testid: "mnav-dashboard" },
    { to: "/merchant/claim", label: t("merchant_nav.file_claim"), icon: FilePlus, testid: "mnav-claim" },
    { to: "/merchant/team", label: "Team", icon: Users, testid: "mnav-team" },
    { to: "/merchant/settings", label: "Settings", icon: SettingsIcon, testid: "mnav-settings" },
    { to: "/merchant/kfs", label: t("merchant_nav.key_facts"), icon: BookOpen, testid: "mnav-kfs" },
  ];

  return (
    <div className="min-h-screen flex merchant-shell" data-testid="merchant-shell">
      <aside
        className="w-64 shrink-0 bg-white flex flex-col"
        style={{ borderRight: "1px solid rgba(17, 66, 50, 0.1)" }}
        data-testid="merchant-sidebar"
      >
        {/* Brand */}
        <div
          className="px-5 py-5 flex items-center gap-2.5"
          style={{ borderBottom: "1px solid rgba(17, 66, 50, 0.08)" }}
        >
          <img src="/fortiqa-mark.svg" alt="Fortiqa" className="w-9 h-9 object-contain shrink-0" data-testid="brand-logo-merchant" />
          <div>
            <div className="font-heading font-bold text-lg tracking-tight text-[#114232]">Fortiqa</div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-[#5c6360] font-semibold">
              {t("common.merchant")}
            </div>
          </div>
        </div>

        {/* Nav */}
        <nav className="flex-1 py-4 px-3">
          {NAV.map(({ to, label, icon: Icon, end, testid }) => (
            <NavLink
              key={to} to={to} end={end} data-testid={testid}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 my-0.5 text-[13px] rounded-lg transition-all ${
                  isActive
                    ? "bg-[#114232] text-[#f4f2eb] font-semibold shadow-sm"
                    : "text-[#5c6360] hover:bg-[#114232]/6 hover:text-[#114232]"
                }`
              }
            >
              <Icon className="w-4 h-4 shrink-0" strokeWidth={1.7} />
              <span className="truncate">{label}</span>
            </NavLink>
          ))}
        </nav>

        {/* Language + user */}
        <div className="p-3" style={{ borderTop: "1px solid rgba(17, 66, 50, 0.08)" }}>
          <div className="px-1 pb-3 flex items-center justify-between">
            <span className="text-[10px] uppercase tracking-[0.18em] text-[#5c6360] font-semibold">
              {t("language.label")}
            </span>
            <LanguageSwitcher compact />
          </div>
          <div className="px-2 py-2 text-xs">
            <div className="text-[#5c6360] uppercase tracking-wider font-semibold">{t("common.signed_in_as")}</div>
            <div className="font-medium text-[#1b1d1c] truncate">{user?.email}</div>
            <div className="text-[#5c6360] mt-0.5">{t("common.merchant")}</div>
          </div>
          <button
            onClick={async () => { await logout(); navigate("/login"); }}
            data-testid="merchant-logout-btn"
            className="mt-2 w-full flex items-center gap-2 px-3 py-2.5 text-[13px] rounded-lg text-[#5c6360] hover:bg-[#c15337]/8 hover:text-[#c15337] transition-colors"
          >
            <LogOut className="w-4 h-4" strokeWidth={1.7} />
            <span>{t("auth.logout")}</span>
          </button>
        </div>
      </aside>
      <main className="flex-1 overflow-x-hidden flex flex-col" data-testid="merchant-main">
        <div className="px-6 sm:px-8 pt-5 flex justify-end items-center gap-2.5" data-testid="merchant-topbar">
          <Link
            to="/"
            data-testid="merchant-view-landing-link"
            title="Preview the public landing page"
            className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full border border-[#114232]/12 bg-white text-xs text-[#5c6360] hover:text-[#114232] hover:border-[#114232]/30 transition-colors"
          >
            <Home className="w-3.5 h-3.5" />
            <span>View landing</span>
          </Link>
          <NotificationsBell />
        </div>
        <Outlet />
      </main>
    </div>
  );
}
